/**
 * Collection of utility functions for handling markdown formatting in textareas.
 * This includes text formatters, selection helpers, and cursor position calculations.
 */

// Basic markdown formatters for common markdown syntax
export const formatters = {
  bold: (text: string) => (text ? `**${text}**` : `****`),
  italic: (text: string) => (text ? `*${text}*` : `**`),
  link: (text: string) => {
    const url = text.match(/^https?:\/\//) ? text : '';
    return text ? `[${text}](${url})` : `[]()`;
  },
  code: (text: string) => (text ? `\`${text}\`` : '``'),
  strikethrough: (text: string) => (text ? `~~${text}~~` : `~~~~`),
  orderedList: (line: string, index?: number) => `${index !== undefined ? index + 1 : 1}. ${line}`,
  unorderedList: (line: string) => `- ${line}`,
  blockquote: (line: string) => `> ${line}`,
};

type PastedMarkdownLinkInput = {
  value: string;
  selectionStart: number;
  selectionEnd: number;
  pastedText: string;
};

type PastedMarkdownLinkResult = {
  value: string;
  selectionStart: number;
};

const getPastedHttpUrl = (pastedText: string): string | null => {
  const trimmedText = pastedText.trim();

  if (!trimmedText || /\s/.test(trimmedText)) {
    return null;
  }

  try {
    const url = new URL(trimmedText);
    return url.protocol === 'http:' || url.protocol === 'https:' ? trimmedText : null;
  } catch {
    return null;
  }
};

export const createMarkdownLinkFromPastedUrl = ({
  value,
  selectionStart,
  selectionEnd,
  pastedText,
}: PastedMarkdownLinkInput): PastedMarkdownLinkResult | null => {
  if (selectionStart === selectionEnd) {
    return null;
  }

  const selectedText = value.slice(selectionStart, selectionEnd);
  const url = getPastedHttpUrl(pastedText);

  if (!selectedText.trim() || !url) {
    return null;
  }

  const markdownLink = `[${selectedText}](${url})`;
  const nextValue = value.slice(0, selectionStart) + markdownLink + value.slice(selectionEnd);

  return {
    value: nextValue,
    selectionStart: selectionStart + markdownLink.length,
  };
};

/** Represents the state of text selection in a textarea */
type TextSelection = {
  beforeText: string;
  selectedText: string;
  afterText: string;
  start: number; // Selection start position
  end: number; // Selection end position
};

/**
 * Extracts the current text selection state from a textarea
 * @returns Object containing the selection details and surrounding text
 */
export const getTextSelection = (textarea: HTMLTextAreaElement): TextSelection => {
  const start = textarea.selectionStart;
  const end = textarea.selectionEnd;
  const selectedText = textarea.value.substring(start, end);
  const beforeText = textarea.value.substring(0, start);
  const afterText = textarea.value.substring(end);

  return { beforeText, selectedText, afterText, start, end };
};

/**
 * Calculates where to place the cursor after a markdown formatting operation
 * For empty formatting markers, places cursor in a user-friendly position (e.g., between ** for bold)
 */
export const calculateNewCursorPosition = (
  start: number,
  selectedText: string,
  formattedText: string,
): number => {
  // Special case: if text was selected and we're creating a link,
  // place cursor inside the parentheses: [selected_text](|)
  if (selectedText && formattedText.startsWith('[') && formattedText.endsWith(']()')) {
    return start + formattedText.length - 1; // -1 to position before the closing )
  }

  // If text was selected, place cursor after the formatted text
  if (selectedText) {
    return start + formattedText.length;
  }

  // Position cursor inside empty formatting markers for better UX
  if (formattedText.startsWith('[')) {
    return start + 1;
  } // []() -> [|]()
  if (formattedText.startsWith('**')) {
    return start + 2;
  } // **** -> **|**
  if (formattedText.startsWith('~~')) {
    return start + 2;
  } // ~~~~ -> ~~|~~
  if (formattedText === '``') {
    return start + 1;
  } // `` -> `|`
  if (formattedText.startsWith('*')) {
    return start + 1;
  } // ** -> *|*

  return start + formattedText.length;
};

/** Represents the state of a line selection in a textarea */
type LineSelection = {
  beforeLineStart: string;
  currentLine: string;
  afterLineEnd: string;
  lineStart: number;
  lineEnd: number;
};

/**
 * Extracts the current line and its surrounding context from a textarea
 * Used for line-based operations like lists and blockquotes
 */
export const getLineSelection = (textarea: HTMLTextAreaElement): LineSelection => {
  const cursorPos = textarea.selectionStart;
  const lastNewline = textarea.value.lastIndexOf('\n', cursorPos - 1);
  const lineStart = lastNewline === -1 ? 0 : lastNewline + 1;
  const lineEnd = textarea.value.indexOf('\n', cursorPos);
  const finalLineEnd = lineEnd === -1 ? textarea.value.length : lineEnd;

  return {
    beforeLineStart: textarea.value.substring(0, lineStart),
    currentLine: textarea.value.substring(lineStart, finalLineEnd),
    afterLineEnd: textarea.value.substring(finalLineEnd),
    lineStart,
    lineEnd: finalLineEnd,
  };
};

/**
 * Updates the textarea value and ensures proper cursor positioning
 * Handles both the React state update and DOM textarea manipulation
 */
export const updateTextAreaValue = (
  textarea: HTMLTextAreaElement,
  newValue: string,
  newCursorPosition: number,
  setTriggerAutoSize: (value: string) => void,
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void,
) => {
  // Update React state
  setTriggerAutoSize(newValue);
  onChange?.({ target: { value: newValue } } as React.ChangeEvent<HTMLTextAreaElement>);

  // Update DOM and cursor position after React render
  requestAnimationFrame(() => {
    try {
      if (textarea && document.body.contains(textarea)) {
        textarea.focus();
        textarea.setSelectionRange(newCursorPosition, newCursorPosition);
      }
    } catch (error) {
      // Silently handle any DOM manipulation errors
      console.debug('Failed to update textarea cursor position:', error);
    }
  });
};
